import { useRef } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';

import { Form, HolderDataForm } from './form';
import { Paxes } from './paxes';
import { PaxResponseDto } from '@bandido/shared/dtos';



const useValidationSchema = () => {
  const { t } = useTranslation();
  return yup.object().shape({
    name: yup.string().required(t('common.required')),
    surname: yup.string().required(t('common.required')),
    email: yup.string().optional().nullable(),
  });
};

interface Props {
  onClose: () => void;
  onContinue: (data: Form) => void;
  paxes: PaxResponseDto[];
}

export const HotelHolderData = ({ onClose, onContinue, paxes }: Props) => {
  const formRef = useRef<HTMLFormElement>(null);
  const validationSchema = useValidationSchema();
  const { t } = useTranslation();

  const {
    control,
    formState,
    handleSubmit,
    setValue,
    reset
  } = useForm<Form>({
    defaultValues: { 
      name: '',
      surname: '',
      email: '',
    },
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = (data: Form) => {
    onContinue(data);
  };

  return (
    <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
      <Dialog open>
        <DialogTitle>{t('saleOrder.view.hotels.holder.title')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} minWidth="400px">
            <Box display="flex" flexDirection="column" gap={2}>
              <Paxes
                setValue={setValue}
                paxes={paxes}
              />
              <HolderDataForm
                control={control}
              />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={
              () => {
                reset();
                onClose();
              }
            }
            variant="outlined"
          >
            {t('common.cancel')}
          </Button>
          <LoadingButton
            variant="contained"
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            disabled={!formState.isValid}
          >
            {t('common.continue')}
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </form>
  );
};
